import React, {ReactElement} from "react"
import {
	Account,
	getProduct,
	hasAccountNumber,
	isDepositAccount,
	isDepositOrBatchOrFBOAccount,
	isDepositOrFBOAccount,
} from "../../resources/account"
import numeral from "numeral"
import {Resource} from "../../resources/common"
import {AccountsColumns, AllowedAccountsColumns} from "./Accounts.types"
import {startCase} from "lodash"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import {useIsBankUser} from "../../services/auth"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import classNames from "classnames"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faUsers} from "@fortawesome/free-solid-svg-icons"
import {OrgName} from "../OrgName/OrgName"
import {BankName} from "../BankName/BankName"
import {accountNumberPreview, InformationHiding} from "../InformationHiding/InformationHiding"
import moment from "moment"
import IdLink from "../IdLink/IdLink"

interface CustomerRowProps {
	account: Account
	include?: Resource[]
	includedColumns: Array<AllowedAccountsColumns>
	refresh?: () => void
}

function getDisplayType(type: string): string {
	return type == "fboAccount" ? "Org FBO Account" : startCase(type)
}

export default function AccountRow({account, include, includedColumns, refresh}: CustomerRowProps) {
	const navigateWithListPath = useNavigateWithListPath()
	const isBankUser = useIsBankUser()

	const id = account.id
	// Filter out account types that do not have org, like OrgLoanAccount and GLAccount
	const org = account.relationships && "org" in account.relationships ? account.relationships.org.data.id : null
	const bank = account.relationships && "bank" in account.relationships ? account.relationships.bank.data.id : null
	const name = account.attributes.name
	const type = account.type
	const createdAt = account.attributes.createdAt
	const updatedAt = account.attributes.updatedAt
	const balance = account.attributes.balance
	const status = account.attributes.closeReason
		? `${account.attributes.status} (${account.attributes.closeReason})`
		: account.attributes.status
	const routingNumber = hasAccountNumber(account) ? account.attributes.routingNumber : "-"
	const product = getProduct(account)
	const accountNumber = hasAccountNumber(account) ? account.attributes.accountNumber : null
	const maskedAccountNumber = isDepositOrBatchOrFBOAccount(account) ? account.attributes.maskedAccountNumber : null
	const purpose = isDepositOrFBOAccount(account) ? account.attributes.tags["purpose"] || "" : null
	const customers = isDepositAccount(account) ? account.relationships.customers : null

	const contentColumns: Partial<Record<AllowedAccountsColumns, ReactElement>> = {}

	if (includedColumns.includes(AccountsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink className={classNames("data-table-id")} href={`/accounts/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountsColumns.type)) {
		contentColumns["Type"] = (
			<DataTableCell>
				{getDisplayType(type)}
				{customers && customers?.data.length > 1 ? (
					<span className="icon ml-2">
						<FontAwesomeIcon size="sm" icon={faUsers} title={"Joint Account"} />
					</span>
				) : (
					<></>
				)}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountsColumns.org)) {
		if (org) {
			contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} element={DataTableCell} />
		} else {
			contentColumns["Org"] = <DataTableCell> -- </DataTableCell>
		}
	}

	if (includedColumns.includes(AccountsColumns.bank)) {
		if (bank) {
			contentColumns["Bank"] = (
				<DataTableCell>
					<BankName bankId={bank.toString()} included={include} />
				</DataTableCell>
			)
		} else {
			contentColumns["Bank"] = <DataTableCell> -- </DataTableCell>
		}
	}

	if (includedColumns.includes(AccountsColumns.name)) {
		contentColumns["Name"] = <DataTableCell>{name}</DataTableCell>
	}

	if (includedColumns.includes(AccountsColumns.balance)) {
		contentColumns["Balance"] = (
			<DataTableCell className={classNames(balance < 0 && "has-text-danger")}>
				{numeral(account.attributes.balance / 100).format("$0,0.00")}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountsColumns.product)) {
		contentColumns["Product"] = <DataTableCell>{product || "-"}</DataTableCell>
	}

	if (includedColumns.includes(AccountsColumns.status)) {
		contentColumns["Status"] = <DataTableCell>{status}</DataTableCell>
	}

	if (includedColumns.includes(AccountsColumns.routingNumber)) {
		contentColumns["Routing Number"] = <DataTableCell>{routingNumber}</DataTableCell>
	}

	if (includedColumns.includes(AccountsColumns.accountNumber)) {
		if (accountNumber) {
			contentColumns["Account Number"] = (
				<DataTableCell>
					<InformationHiding
						element={(accountNumber) => <>{accountNumber}</>}
						getValue={() => accountNumber ?? ""}
						placeholder={accountNumberPreview(accountNumber)}
						defaultIsHidden={!isBankUser}
					/>
				</DataTableCell>
			)
		} else {
			if (maskedAccountNumber) {
				contentColumns["Account Number"] = <DataTableCell> {maskedAccountNumber.replace(/[*]/g, "●")} </DataTableCell>
			} else {
				contentColumns["Account Number"] = <DataTableCell>{"-"}</DataTableCell>
			}
		}
	}

	if (includedColumns.includes(AccountsColumns.purpose)) {
		contentColumns["Purpose"] = <DataTableCell>{purpose || "-"}</DataTableCell>
	}

	if (includedColumns.includes(AccountsColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(AccountsColumns.updatedAt)) {
		contentColumns["Updated At"] = (
			<DataTableCell>
				{updatedAt ? moment(updatedAt).format("L LT") : <span className="empty-date-placeholder"> --/--/---- </span>}
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})
	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigateWithListPath(`/accounts/${id}`)
				refresh && refresh()
			}}
		>
			{content}
		</DataTableRow>
	)
}
